import { env } from './env';
import { logger } from './logger';
import { unwrapNestedApiPayload } from './unwrapApiData';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(status: number, message: string, code?: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

const TENANT_SCOPED_PATH_PREFIXES = [
  '/api/v1/subscriptions',
  '/api/v1/payments',
  '/api/v1/customer',
  '/api/v1/me',
];

let resolveActiveTenantId: () => string | null = () => null;

export function setTenantIdProvider(provider: () => string | null): void {
  resolveActiveTenantId = provider;
}

function pathRequiresTenant(path: string): boolean {
  return TENANT_SCOPED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// ── Refresh token deduplication ───────────────────────────────────────────────
// A single in-flight refresh promise, shared across all concurrent 401 failures.
let refreshPromise: Promise<void> | null = null;

async function refreshTokens(): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(`${env.VITE_API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => {
      if (!res.ok) throw new ApiError(res.status, 'Session expired');
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// ── Core request function ─────────────────────────────────────────────────────

async function request<T>(path: string, options: RequestOptions = {}, _retry = true): Promise<T> {
  const { params, ...init } = options;

  const url = new URL(path, env.VITE_API_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const isFormData = init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init.headers as Record<string, string> | undefined),
  };

  if (pathRequiresTenant(path)) {
    const tenantId = resolveActiveTenantId();
    if (!tenantId) {
      window.dispatchEvent(new Event('tenant:requireSelection'));
      throw new ApiError(400, 'Select a tenant before continuing.', 'TENANT_CONTEXT_REQUIRED');
    }
    headers['X-Tenant-Id'] = tenantId;
  }

  const response = await fetch(url.toString(), {
    ...init,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    // 401 → attempt one silent token refresh then retry
    if (response.status === 401 && _retry) {
      try {
        await refreshTokens();
        return request<T>(path, options, false);
      } catch {
        window.dispatchEvent(new Event('auth:logout'));
        throw new ApiError(401, 'Session expired. Please sign in again.', 'SESSION_EXPIRED');
      }
    }

    const errorBody = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    logger.error(`API error ${response.status}`, errorBody);

    // Support both flat `{ message, code }` and nested `{ error: { code, message, retry_after_seconds } }` shapes
    const nested = errorBody?.['error'] as Record<string, unknown> | undefined;
    const code = (nested?.['code'] ?? errorBody?.['code']) as string | undefined;
    const message =
      ((nested?.['message'] ?? errorBody?.['message']) as string | undefined) ??
      response.statusText;
    const retryAfter = nested?.['retry_after_seconds'] as number | undefined;

    if (code === 'TENANT_MISMATCH') {
      window.dispatchEvent(new Event('auth:logout'));
    }

    throw new ApiError(response.status, message, code, retryAfter);
  }

  const body = (await response.json()) as T;
  return unwrapNestedApiPayload(body);
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body: JSON.stringify(body) }),
  postForm: <T>(path: string, body: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
